import { useQuery } from "@tanstack/react-query";
import { Info, Lock } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { FeatureGate } from "~/components/billing/feature-gate";
import { DataLoadError } from "~/components/ui/data-load-error";
import { envConfig } from "~/lib/env-validation";
import type { FinancialsByYearResponse, FinancialsSummaryResponse } from "~/types/financials";
import { AssetCompositionChart } from "../financials/asset-composition-chart";
import { BalanceSheetTable } from "../financials/balance-sheet-table";
import { BalanceTrendChart } from "../financials/balance-trend-chart";
import { ExportButtons } from "../financials/export-buttons";
import { BalanceSheetKpiCards, IncomeStatementKpiCards } from "../financials/financial-kpi-cards";
import { LineItemsTable } from "../financials/line-items-table";
import { ProfitLossTable } from "../financials/profit-loss-table";
import { RatioCards } from "../financials/ratio-cards";
import { RatioTrendChart } from "../financials/ratio-trend-chart";
import { RevenueChart } from "../financials/revenue-chart";

interface FinancialsTabProps {
	summary: FinancialsSummaryResponse;
}

async function fetchByYear(ico: string, year: number): Promise<FinancialsByYearResponse> {
	const res = await fetch(`${envConfig.apiBaseUrl}/companies/${ico}/financials/${year}`);
	if (!res.ok) throw new Error("Nepodařilo se načíst detail roku");
	return res.json();
}

export function FinancialsTab({ summary }: FinancialsTabProps) {
	const [selectedYear, setSelectedYear] = useState<number | null>(null);

	const { ico, years, truncated, viewerTier, availableYears: totalAvailable } = summary;

	const detailQuery = useQuery({
		queryKey: ["financials-year", ico, selectedYear],
		queryFn: () => fetchByYear(ico, selectedYear as number),
		enabled: selectedYear != null,
	});

	if (years.length === 0) {
		return (
			<div className="py-8 text-center text-muted-foreground">
				<p className="text-sm">Finanční data nejsou k dispozici.</p>
			</div>
		);
	}

	const availableYears = years.map((y) => y.year);
	const hiddenYears = Math.max(0, totalAvailable - years.length);
	const balanceYears = years.filter((year) => year.availability.balanceSheet);
	const incomeYears = years.filter((year) => year.availability.profitLoss);
	const latestBalance = balanceYears.at(-1) ?? null;
	const latestIncome = incomeYears.at(-1) ?? null;
	const hasMixedAvailability =
		latestBalance != null && (latestIncome == null || latestIncome.year < latestBalance.year);

	return (
		<div className="flex flex-col gap-6 pt-4">
			{/* Only anon/free can actually unlock more history (free 3y, paid 5y).
			    Premium/Pro/Enterprise already get the max 5 years, so showing them an
			    "Upgradovat" upsell to a plan they own is misleading — suppress it. */}
			{truncated && hiddenYears > 0 && (viewerTier === "anon" || viewerTier === "free") && (
				<div className="rounded-lg border border-dashed border-primary-200 bg-primary-50 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
					<div className="flex items-start gap-3">
						<Lock className="h-4 w-4 text-primary-600 mt-0.5 shrink-0" />
						<div className="flex flex-col">
							<p className="text-sm font-medium text-foreground">
								{viewerTier === "anon"
									? `${hiddenYears < 5 ? "Další" : "Dalších"} ${hiddenYears} ${hiddenYears === 1 ? "rok" : hiddenYears < 5 ? "roky" : "let"} historie zdarma po registraci`
									: `${hiddenYears < 5 ? "Další" : "Dalších"} ${hiddenYears} ${hiddenYears === 1 ? "rok" : hiddenYears < 5 ? "roky" : "let"} historie v plánu Premium`}
							</p>
							<p className="text-xs text-muted-foreground mt-0.5">
								{viewerTier === "anon"
									? "Po registraci uvidíte 3 roky historie. Premium odemkne 5 let."
									: "Premium plán odemkne plnou 5letou historii."}
							</p>
						</div>
					</div>
					<Link
						to={viewerTier === "anon" ? "/login?mode=register" : "/cenik"}
						className="btn btn-sm btn-primary shrink-0"
					>
						{viewerTier === "anon" ? "Zaregistrovat se" : "Upgradovat"}
					</Link>
				</div>
			)}

			{hasMixedAvailability && (
				<div className="card-tonal flex items-start gap-3 rounded-xl p-4">
					<Info className="mt-0.5 h-4 w-4 shrink-0 text-primary-700" />
					<div className="flex flex-col">
						<p className="label-overline">Rozsah zveřejněných dat</p>
						<p className="mt-1 text-sm font-medium text-foreground">
							Rozvaha je dostupná do roku {latestBalance.year}
						</p>
						<p className="mt-0.5 text-xs text-muted-foreground">
							{latestIncome
								? `Výkaz zisku a ztráty společnost naposledy zveřejnila za rok ${latestIncome.year}. Novější dokumenty obsahují pouze rozvahu.`
								: "Výkaz zisku a ztráty není v dostupné historii zveřejněn."}
						</p>
					</div>
				</div>
			)}

			{latestBalance && (
				<section aria-labelledby="balance-sheet-heading" className="flex flex-col gap-6">
					<div>
						<p className="label-overline">Nejnovější údaje za rok {latestBalance.year}</p>
						<h2 id="balance-sheet-heading" className="mt-1 text-xl font-semibold">
							Rozvaha
						</h2>
					</div>
					<BalanceSheetKpiCards years={balanceYears} />
					<BalanceTrendChart years={balanceYears} />
					<AssetCompositionChart year={latestBalance} />
					<BalanceSheetTable years={balanceYears} />
				</section>
			)}

			{latestIncome && (
				<section
					aria-labelledby="profit-loss-heading"
					className="flex flex-col gap-6 border-t border-border pt-6"
				>
					<div>
						<p className="label-overline">Naposledy zveřejněn za rok {latestIncome.year}</p>
						<h2 id="profit-loss-heading" className="mt-1 text-xl font-semibold">
							Výkaz zisku a ztráty
						</h2>
					</div>
					<IncomeStatementKpiCards years={incomeYears} />
					<RevenueChart years={incomeYears} />
					<ProfitLossTable years={incomeYears} />
				</section>
			)}

			<FeatureGate minPlan="PREMIUM">
				<RatioCards years={years} />
			</FeatureGate>

			<FeatureGate minPlan="PREMIUM">
				<RatioTrendChart years={years} />
			</FeatureGate>

			{/* Premium tier: Detailed line items */}
			<FeatureGate minPlan="PREMIUM">
				<div className="card-surface rounded-xl p-6">
					<div className="flex items-center justify-between mb-4">
						<h3 className="text-lg font-semibold">Detailní výkaz</h3>
						<select
							value={selectedYear ?? ""}
							onChange={(e) => {
								const val = e.target.value;
								setSelectedYear(val ? Number(val) : null);
							}}
							className="select select-bordered select-sm"
						>
							<option value="">Vyberte rok</option>
							{availableYears.map((y) => (
								<option key={y} value={y}>
									{y}
								</option>
							))}
						</select>
					</div>

					{selectedYear == null && (
						<p className="text-sm text-muted-foreground">
							Vyberte rok pro zobrazení detailních položek výkazu.
						</p>
					)}

					{selectedYear != null && detailQuery.status === "pending" && (
						<div className="flex items-center justify-center py-8">
							<span className="loading loading-spinner loading-md" />
						</div>
					)}

					{selectedYear != null && detailQuery.status === "error" && (
						<DataLoadError
							title="Detail roku se nepodařilo načíst"
							description="Souhrnné finanční údaje zůstávají dostupné."
							onRetry={() => detailQuery.refetch()}
							retrying={detailQuery.fetchStatus === "fetching"}
						/>
					)}

					{selectedYear != null && detailQuery.status === "success" && (
						<LineItemsTable lineItems={detailQuery.data.lineItems} />
					)}
				</div>
			</FeatureGate>

			{/* Export buttons (self-gated) */}
			<div className="flex items-center justify-between">
				<p className="text-sm text-muted-foreground">Export finančních dat</p>
				<ExportButtons ico={ico} />
			</div>
		</div>
	);
}
